import { EVENTS, type EventDef } from '../data/events';
import { DISTRICTS, getDistrict } from '../data/districts';
import type { GameState } from '../models/GameState';
import type { GameEvent, Notification } from '../models/GameEvent';
import type { GameTime } from '../models/GameTime';
import { clamp } from '../utils/math';
import type { RandomGenerator } from '../utils/random';

const MAX_NOTIFICATIONS = 20;

/**
 * EventEngine (spec sections 22, 36): rolls random events once per day, expires
 * finished ones, and exposes the demand multiplier active in a district. Pure &
 * deterministic (rng-driven). Effects are applied by callers (demand in the
 * spawn path; reputation deltas at start).
 */
export const EventEngine = {
  /** Remove events whose window has ended. */
  expire(state: GameState, nowMinutes: number): void {
    if (state.events.length === 0) return;
    state.events = state.events.filter((e) => e.endMinute > nowMinutes);
  },

  /**
   * Roll for each event definition (once per day). Newly started events are
   * pushed onto state.events, their reputation deltas applied to affected player
   * lots, and a notification queued. Returns the started events.
   */
  rollDaily(state: GameState, time: GameTime, rng: RandomGenerator): GameEvent[] {
    const started: GameEvent[] = [];
    for (const def of EVENTS) {
      if (!rng.chance(def.probabilityPerDay)) continue;
      const event = this.activate(def, time, rng);
      state.events.push(event);
      started.push(event);
      this.applyStartEffects(state, def, event);
      this.notify(state, {
        id: `n-${event.id}`,
        message: event.message,
        kind: def.kind,
        atMinute: time.totalMinutes,
      });
    }
    return started;
  },

  activate(def: EventDef, time: GameTime, rng: RandomGenerator): GameEvent {
    const hours = rng.nextInt(def.minDurationHours, def.maxDurationHours);
    const durationMinutes = hours * 60;

    let districtId = 'city';
    let districtName = 'the city';
    if (def.scope === 'district') {
      const pool = def.districts ?? DISTRICTS.map((d) => d.id);
      districtId = pool[rng.nextInt(0, pool.length - 1)];
      districtName = getDistrict(districtId)?.name ?? districtId;
    }

    return {
      id: `${def.id}-${time.totalMinutes}`,
      defId: def.id,
      name: def.name,
      kind: def.kind,
      districtId,
      startMinute: time.totalMinutes,
      endMinute: time.totalMinutes + durationMinutes,
      demandMultiplier: def.demandMultiplier,
      message: def.message.replace('{district}', districtName),
    };
  },

  /** One-time effects at event start (e.g. a reputation hit). */
  applyStartEffects(state: GameState, def: EventDef, event: GameEvent): void {
    if (!def.reputationDelta) return;
    for (const lot of state.parkingLots) {
      if (event.districtId === 'city' || lot.districtId === event.districtId) {
        lot.reputation = clamp(lot.reputation + def.reputationDelta, 0, 100);
      }
    }
  },

  notify(state: GameState, n: Notification): void {
    state.notifications.push(n);
    if (state.notifications.length > MAX_NOTIFICATIONS) {
      state.notifications.splice(0, state.notifications.length - MAX_NOTIFICATIONS);
    }
  },

  /** Combined demand multiplier from all events active in a district now. */
  demandMultiplier(state: GameState, districtId: string, nowMinutes: number): number {
    let mult = 1;
    for (const e of state.events) {
      if (e.startMinute > nowMinutes || e.endMinute <= nowMinutes) continue;
      if (e.districtId === 'city' || e.districtId === districtId) {
        mult *= e.demandMultiplier;
      }
    }
    return mult;
  },

  /** Events active right now that affect a given district (for UI). */
  activeInDistrict(state: GameState, districtId: string, nowMinutes: number): GameEvent[] {
    return state.events.filter(
      (e) =>
        e.startMinute <= nowMinutes &&
        e.endMinute > nowMinutes &&
        (e.districtId === 'city' || e.districtId === districtId)
    );
  },

  /** All events active right now (for a city-wide banner). */
  active(state: GameState, nowMinutes: number): GameEvent[] {
    return state.events.filter(
      (e) => e.startMinute <= nowMinutes && e.endMinute > nowMinutes
    );
  },
};
